import {
  BottomSheetBackdrop,
  BottomSheetHandle,
  BottomSheetModal,
  BottomSheetView,
  type BottomSheetBackdropProps,
  type BottomSheetHandleProps,
  type BottomSheetModalProps
} from '@gorhom/bottom-sheet'
import { IconCameraSelfie, IconLibraryPhoto } from '@tabler/icons-react-native'
import { forwardRef, useCallback } from 'react'

import { BottomSheetHeader, Icon, Pressable, Text } from '@/components'
import { useBottomSheetContainerStyle, useFocusableRef } from '@/hooks'
import {
  blackBottomSheetModalBackgroundStyle,
  blackBottomSheetModalHandleIndicatorStyle,
  blackBottomSheetModalHandleStyle,
  IS_WEB
} from '@/lib'

export type ChooseMediaBottomSheetRef = BottomSheetModal
export interface ChooseMediaBottomSheetProps extends Omit<BottomSheetModalProps, 'children'> {
  isDisabled?: boolean
  onCameraPress: () => Promise<void>
  onClosePress: () => void
  onLibraryPress: () => Promise<void>
}

export const ChooseMediaBottomSheet = forwardRef<BottomSheetModal, ChooseMediaBottomSheetProps>(
  ({ containerStyle, isDisabled, onCameraPress, onClosePress, onLibraryPress, ...props }, ref) => {
    const [headerRef, focusHeader] = useFocusableRef()
    const [optionRef, focusOption] = useFocusableRef()

    return (
      <BottomSheetModal
        accessible={false}
        backdropComponent={useCallback(
          (props: BottomSheetBackdropProps) => (
            <BottomSheetBackdrop
              {...props}
              accessible={false}
              accessibilityLabel=''
              accessibilityRole='none'
              appearsOnIndex={0}
              disappearsOnIndex={-1}
              importantForAccessibility='no-hide-descendants'
            />
          ),
          []
        )}
        backgroundStyle={blackBottomSheetModalBackgroundStyle}
        containerStyle={useBottomSheetContainerStyle(containerStyle)}
        enableDismissOnClose
        enableDynamicSizing
        enablePanDownToClose
        handleComponent={useCallback(
          (props: BottomSheetHandleProps) => (
            <BottomSheetHandle
              {...props}
              accessible={false}
              accessibilityLabel=''
              accessibilityRole='none'
              importantForAccessibility='no-hide-descendants'
            />
          ),
          []
        )}
        handleIndicatorStyle={blackBottomSheetModalHandleIndicatorStyle}
        handleStyle={blackBottomSheetModalHandleStyle}
        ref={ref}
        onChange={useCallback(
          (position: number) => {
            // opened
            if (position === 0) {
              if (IS_WEB) {
                focusOption()
              } else {
                focusHeader()
              }
            }
          },
          [focusHeader, focusOption]
        )}
        {...props}
      >
        <BottomSheetView className='pb-safe-or-0'>
          <BottomSheetHeader
            accessibilityLabel='Add Photo Options'
            onClosePress={onClosePress}
            ref={headerRef}
            title='Add Photo'
          />
          <Pressable
            accessibilityHint='Take a photo with your camera'
            accessibilityRole='button'
            className='min-h-10 flex-1 flex-row items-center gap-2 px-4 hover:bg-background-50 active:bg-background-100 data-[focus=true]:bg-background-100 web:data-[focus-visible=true]:bg-background-100 web:data-[focus-visible=true]:outline-indicator-primary data-[disabled=true]:opacity-40 data-[disabled=true]:web:pointer-events-auto data-[disabled=true]:web:cursor-not-allowed'
            disabled={isDisabled}
            onPress={onCameraPress}
            ref={optionRef}
            testID='ChooseMediaBottomSheet.cameraButton'
          >
            <Icon
              as={IconCameraSelfie}
              className='color-white'
              size='xl'
            />
            <Text
              className='color-white'
              size='lg'
            >
              Take Selfie
            </Text>
          </Pressable>
          <Pressable
            accessibilityHint='Choose a photo from your library'
            accessibilityRole='button'
            className='min-h-10 flex-1 flex-row items-center gap-2 px-4 hover:bg-background-50 active:bg-background-100 data-[focus=true]:bg-background-100 web:data-[focus-visible=true]:bg-background-100 web:data-[focus-visible=true]:outline-indicator-primary data-[disabled=true]:opacity-40 data-[disabled=true]:web:pointer-events-auto data-[disabled=true]:web:cursor-not-allowed'
            disabled={isDisabled}
            onPress={onLibraryPress}
            testID='ChooseMediaBottomSheet.libraryButton'
          >
            <Icon
              as={IconLibraryPhoto}
              className='color-white'
              size='xl'
            />
            <Text
              className='color-white'
              size='lg'
            >
              Choose Photo
            </Text>
          </Pressable>
        </BottomSheetView>
      </BottomSheetModal>
    )
  }
)

ChooseMediaBottomSheet.displayName = 'ChooseMediaBottomSheet'
